'use client'

import React, { useState, useEffect } from 'react'
import Button from '@/components/ui/Button'
import { useCookieConsent } from '@/context/cookie-consent-context/CookieConsentContext'
// import { X } from 'lucide-react'
import Link from 'next/link'
import Image from 'next/image'

export function CookieBanner() {
  const { 
    showBanner, 
    acceptAll, 
    rejectAll, 
    openPreferences, 
    // closeBanner 
  } = useCookieConsent()

  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    if (showBanner) {
      // Small delay to ensure smooth entrance
      const timer = setTimeout(() => setIsAnimating(true), 100)
      return () => clearTimeout(timer)
    } else {
      setIsAnimating(false)
    }
  }, [showBanner])

  const handleAction = (action: () => void) => {
    setIsAnimating(false)
    setTimeout(action, 200)
  }

  const handlePreferences = () => {
    // Don't hide banner when opening preferences - just open modal
    openPreferences()
  }

  if (!showBanner) return null

  return (
    <div 
      className={`fixed inset-x-0 z-50 flex items-end justify-center px-4 transition-all duration-200 ease-out ${
        isAnimating 
          ? 'translate-y-0 opacity-100' 
          : 'translate-y-4 opacity-0'
      }`}
      style={{ bottom: '34px' }}
    >
      <div className="w-full max-w-6xl">
        {/* Close Button */}
        {/* <button
            onClick={closeBanner}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 
            transition-colors rounded-lg hover:bg-gray-50"
            aria-label="Close cookie banner"
          >
            <X className="w-5 h-5" />
        </button> */}
        <div className="bg-white rounded-16 shadow-s500 border border-gray-100">
          <div className="flex items-center p-4 gap-4">
            {/* Cookie Icon */}
            <div className="flex-shrink-0">
              <Image
                src="/images/cookie-icon.svg"
                alt="Cookie"
                width={48}
                height={48}
                className="w-12 h-12"
              />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-regular-normal text-gray-500 text-pretty">
                {/* We use cookies to improve your experience on Waltz AI.  Cookies help us 
                personalize content, analyze traffic, and enhance platform performance. By 
                continuing to use our site, you agree to our use of cookies.{' '} */}
                We&apos;d like to use other cookies to analyse our website&apos;s performance and personalise ads, but only if you accept. Read our{' '}
                <Link 
                  href="/cookie-policy" 
                  className="link-sm regular-normal"
                >
                  cookie policy
                </Link>
                .
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center flex-shrink-0 gap-4">
              <Button 
                onClick={() => handlePreferences()}
                variant="ghost"
                size="outline"
              >
                Customize
              </Button>

              <Button 
                onClick={() => handleAction(rejectAll)}
                variant="outline"
                size="outline"
              >
                Reject All
              </Button>
              
              <Button 
                onClick={() => handleAction(acceptAll)}
                variant="orange"
                size="outline"
              >
                Accept All
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Compact banner for mobile devices
export function CompactCookieBanner() {
  const { 
    showBanner, 
    acceptAll, 
    rejectAll, 
    openPreferences 
  } = useCookieConsent()

  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    if (showBanner) {
      const timer = setTimeout(() => setIsAnimating(true), 100)
      return () => clearTimeout(timer)
    } else {
      setIsAnimating(false)
    }
  }, [showBanner])

  const handleAction = (action: () => void) => {
    setIsAnimating(false)
    setTimeout(action, 200)
  }

  const handlePreferences = () => {
    // Don't hide banner when opening preferences - just open modal
    openPreferences()
  }

  if (!showBanner) return null

  return (
    <div 
      className={`fixed inset-x-0 z-50 px-4 sm:hidden transition-all duration-200 ease-out ${
        isAnimating 
          ? 'translate-y-0 opacity-100' 
          : 'translate-y-4 opacity-0'
      }`}
      style={{ bottom: '44px' }}
    >
      <div className="bg-white p-4 rounded-16 shadow-s500 border border-gray-100">
        {/* Cookie Icon and Title */}
        <div className="flex items-center gap-3">
          <Image
            src="/images/cookie-icon.svg"
            alt="Cookie"
            width={32}
            height={32}
            className="w-8 h-8"
          />
        </div>

        {/* Title */}
        {/* <h6 className="text-gray-900 text-h6 mt-6">Cookie Settings</h6> */}

        {/* Description */}
        <p className="text-gray-500 regular-normal mt-6 text-pretty">
          We&apos;d like to use other cookies to analyse our website&apos;s performance and personalise ads, but only if you accept. Read our{' '}
          <Link 
            href="/cookie-policy" 
            className="link-sm text-primary"
          >
            cookie policy
          </Link>
          .
        </p>
        
        {/* Buttons */}
        <div className="flex gap-3 mt-6">
          <Button 
            onClick={() => handlePreferences()}
            variant="ghost"
            size="outline"
            className="w-full text-gray-500"
          >
            Customize
          </Button>
          <Button 
            onClick={() => handleAction(rejectAll)}
            variant="outline"
            size="outline"
            className="w-full text-gray-500"
          >
            Reject All
          </Button>
          <Button 
            onClick={() => handleAction(acceptAll)}
            variant="orange"
            size="outline"
            className="w-full"
          >
            Accept All
          </Button>
        </div>
      </div>
    </div>
  )
} 
